import { useCallback, useEffect, useRef, useState } from 'react';

import { useAppDispatch, useAppSelector } from '@/Application/Hooks/Redux';
import type { RootState } from '@/Infrastructure/State';
import { getBuyNowAlternativePayments } from '@/modules/buy-now/infrastructure/BuyNowReduxRepository';
import type { CartPayments } from '@/modules/cart/domain/CartPayments';

export const selectBuyNowAlternativePaymentsData = (state: RootState) =>
  state.buyNow.alternativePayments.data;
export const selectBuyNowAlternativePaymentsLoading = (state: RootState) =>
  state.buyNow.alternativePayments.isLoading;
export const selectBuyNowAlternativePaymentsError = (state: RootState) =>
  state.buyNow.alternativePayments.error;
export const selectBuyNowAlternativePaymentsStatusCode = (state: RootState) =>
  state.buyNow.alternativePayments.statusCode;
export const selectBuyNowAlternativePaymentsRetryCount = (state: RootState) =>
  state.buyNow.alternativePayments.retryCount;

const useBuyNowAlternativePayments = () => {
  const dispatch = useAppDispatch();
  const data = useAppSelector(selectBuyNowAlternativePaymentsData);
  const isLoading = useAppSelector(selectBuyNowAlternativePaymentsLoading);
  const error = useAppSelector(selectBuyNowAlternativePaymentsError);
  const statusCode = useAppSelector(selectBuyNowAlternativePaymentsStatusCode);
  const retryCount = useAppSelector(selectBuyNowAlternativePaymentsRetryCount);
  const [isError, setIsError] = useState<boolean>(Boolean(error));
  const hasFetchedRef = useRef<Set<string>>(new Set());

  const fetchAlternativePayments = useCallback(
    async (cartId: string) => {
      if (hasFetchedRef.current.has(cartId)) {
        return;
      }
      hasFetchedRef.current.add(cartId);
      const { payload } = await dispatch(getBuyNowAlternativePayments(cartId));
      if (payload) {
        setIsError(false);
      }
      return payload as CartPayments;
    },
    [dispatch]
  );

  useEffect(() => {
    if (error) {
      setIsError(true);
    }
  }, [error]);

  return {
    data,
    isLoading,
    error,
    statusCode,
    retryCount,
    isError,
    refetch: fetchAlternativePayments
  };
};

export default useBuyNowAlternativePayments;
